/**
 * Onboarding API Service
 * Handles all API calls related to the onboarding flow
 * Uses nodeAxiosFirebase for consistent API handling
 */

import axios from 'axios';
import nodeAxiosFirebase from '../utils/nodeAxiosFirebase';
import { auth } from '../firebase';
import { getDeviceHeaders } from '../utils/deviceInfo';

const VALIDATE_USER_TIMEOUT_MS = 15000;

// Portail: si le client est connecté, on envoie le Firebase idToken (+ businessToken);
// sinon on reste anonyme (lien token) comme avant.
const clientAuth = () => ({ noAuth: !auth?.currentUser });

/**
 * Récupère et stocke le businessToken de l'entreprise de l'onboarding pour le client connecté.
 * @param {string} businessId
 * @returns {Promise<boolean>} true si le token a été résolu et stocké
 */
export async function resolveOnboardingBusinessToken(businessId) {
  const user = auth?.currentUser;
  if (!user || !businessId) return false;
  const idToken = await user.getIdToken(true);
  const response = await axios({
    method: 'POST',
    url: 'https://api.usenode.com/users/business-token',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}`, ...getDeviceHeaders(user.uid) },
    data: { businessId },
    timeout: VALIDATE_USER_TIMEOUT_MS,
  });
  const match = response?.data?.access;
  if (!match?.token) return false;
  localStorage.setItem('businessId', businessId);
  localStorage.setItem('businessToken', match.token);
  return true;
}

/**
 * Fetch onboarding data
 * @param {string} onboardingId - The onboarding ID
 * @param {string} token - Optional access token
 * @param {object} options - Optional options (t for translations)
 */
export async function fetchOnboarding(onboardingId, token = null, options = {}) {
  if (!onboardingId || onboardingId === 'undefined' || onboardingId === 'null') {
    throw new Error('Missing onboardingId');
  }
  const params = new URLSearchParams({ onboardingId });
  if (token) params.append('token', token);

  const result = await nodeAxiosFirebase({
    method: 'GET',
    url: `https://public.usenode.com/onboarding?${params}`,
    ...clientAuth(),
    ...options,
  });

  if (result?.errorData) {
    throw new Error(result.errorData.message || 'Failed to fetch onboarding');
  }

  // If backend returns { message: 'Success', data: { ... } }, extract data
  if (result?.data?.data && result?.message === 'Success') {
    return result.data.data;
  }

  return result?.data || result;
}

/**
 * Find an existing onboarding by bookingId or email
 * @param {{ bookingId?: string, email?: string }} query
 * @param {object} options
 */
export async function findOnboarding({ bookingId, email } = {}, options = {}) {
  const result = await nodeAxiosFirebase({
    method: 'POST',
    url: 'https://public.usenode.com/onboarding/find',
    body: { bookingId, email },
    noAuth: true,
    ...options,
  });

  if (result?.errorData) {
    throw new Error(result.errorData.message || 'Failed to find onboarding');
  }

  if (result?.message === 'Success') {
    return result.data || null;
  }

  return result?.data || result || null;
}

/**
 * Create a draft onboarding (lightweight, no side effects)
 * @param {object} formData - Partial form data
 * @param {object} options - Optional options
 */
export async function createDraft(formData, options = {}) {
  const result = await nodeAxiosFirebase({
    method: 'POST',
    url: 'https://public.usenode.com/onboarding/draft',
    body: formData,
    noAuth: true,
    ...options,
  });

  if (result?.errorData) {
    throw new Error(result.errorData.message || 'Failed to create draft');
  }

  return result?.data || result;
}

/**
 * Create a new onboarding
 * @param {object} userData - User data for creating onboarding
 * @param {object} options - Optional options (t for translations, showLoading, etc.)
 */
export async function createOnboarding(userData, options = {}) {
  const result = await nodeAxiosFirebase({
    method: 'POST',
    url: 'https://public.usenode.com/onboarding/create',
    body: userData,
    noAuth: true,
    ...options,
  });

  if (result?.errorData) {
    throw new Error(result.errorData.message || 'Failed to create onboarding');
  }

  return result?.data || result;
}

/**
 * Start onboarding (send welcome email for DemoPage created onboardings)
 * @param {string} onboardingId - The onboarding ID
 * @param {object} options - Optional options
 */
export async function startOnboarding(onboardingId, options = {}) {
  const result = await nodeAxiosFirebase({
    method: 'POST',
    url: 'https://public.usenode.com/onboarding/start',
    body: { onboardingId },
    noAuth: true,
    ...options,
  });

  if (result?.errorData) {
    throw new Error(result.errorData.message || 'Failed to start onboarding');
  }

  return result?.data || result;
}

/**
 * Transition onboarding to next state
 * @param {string} onboardingId - The onboarding ID
 * @param {string} action - The transition action
 * @param {object} data - Optional data for the transition
 * @param {object} options - Optional options
 */
export async function transitionOnboarding(onboardingId, action, data = {}, options = {}) {
  const result = await nodeAxiosFirebase({
    method: 'POST',
    url: 'https://public.usenode.com/onboarding/transition',
    body: {
      onboardingId,
      action,
      data,
    },
    noAuth: true,
    ...options,
  });

  if (result?.errorData) {
    throw new Error(result.errorData.message || 'Failed to transition onboarding');
  }

  return result;
}

/**
 * Update onboarding data (PATCH)
 * @param {string} onboardingId - The onboarding ID
 * @param {object} updates - Fields to update
 * @param {string} token - Access token for authentication
 * @param {object} options - Optional options
 */
export async function patchOnboarding(onboardingId, updates, token, options = {}) {
  const result = await nodeAxiosFirebase({
    method: 'PATCH',
    url: 'https://public.usenode.com/onboarding',
    body: {
      onboardingId,
      token,
      ...updates,
    },
    ...clientAuth(),
    ...options,
  });

  if (result?.errorData) {
    throw new Error(result.errorData.message || 'Failed to update onboarding');
  }

  return result;
}

/**
 * Save diagnostic question answer
 * @param {string} onboardingId - The onboarding ID
 * @param {string} questionId - The question ID
 * @param {any} answer - The answer value
 * @param {object} options - Optional options
 */
export async function saveAnswer(onboardingId, questionId, answer, options = {}) {
  const result = await nodeAxiosFirebase({
    method: 'POST',
    url: 'https://public.usenode.com/onboarding/answer',
    body: {
      onboardingId,
      questionId,
      answer,
    },
    noAuth: true,
    ...options,
  });

  if (result?.errorData) {
    throw new Error(result.errorData.message || 'Failed to save answer');
  }

  return result;
}

/**
 * Update structured task
 * @param {string} onboardingId - The onboarding ID
 * @param {string} taskId - The task ID
 * @param {object} updates - Updates to apply to the task
 * @param {object} options - Optional options
 */
export async function updateStructuredTask(onboardingId, taskId, updates, token = null, options = {}) {
  const result = await nodeAxiosFirebase({
    method: 'POST',
    url: 'https://public.usenode.com/onboarding/task/structured',
    body: {
      onboardingId,
      token,
      taskId,
      updates,
      projectId: updates?.projectId || options.projectId || null,
    },
    ...clientAuth(),
    ...options,
  });

  if (result?.errorData) {
    throw new Error(result.errorData.message || 'Failed to update structured task');
  }

  return result;
}

/**
 * Liste les projets BOS (grids) liés à un onboarding et/ou un businessId
 */
export async function listProjects(onboardingId, token = null, options = {}) {
  const businessId = options.businessId || null;
  const result = await nodeAxiosFirebase({
    method: 'POST',
    url: 'https://public.usenode.com/onboarding/projects/list',
    body: { onboardingId, token, businessId },
    noAuth: true,
    ...options,
  });
  if (result?.errorData) throw new Error(result.errorData.message || 'Failed to list projects');
  return result?.data?.data || result?.data || result;
}

/**
 * Met à jour une tâche enfant d'un projet BOS (portail client)
 */
export async function updateProjectTask(onboardingId, projectId, taskId, updates, token = null, options = {}) {
  const result = await nodeAxiosFirebase({
    method: 'POST',
    url: 'https://public.usenode.com/onboarding/projects/task/update',
    body: { onboardingId, token, projectId, taskId, updates },
    ...clientAuth(),
    ...options,
  });
  if (result?.errorData) throw new Error(result.errorData.message || 'Failed to update project task');
  return result?.data?.data || result?.data || result;
}

/**
 * Admin — lie un projet grids existant à un onboarding
 */
export async function adminLinkProject(onboardingId, projectId, { asMain = false, projectType = null } = {}) {
  const result = await nodeAxiosFirebase({
    method: 'POST',
    url: 'private/onboarding',
    body: {
      path: '/onboarding/admin/projects/link',
      onboardingId,
      projectId,
      asMain,
      projectType,
    },
  });
  if (result?.errorData) throw new Error(result.errorData.message || 'Failed to link project');
  return result?.data?.data || result?.data || result;
}

/**
 * Admin — détache un projet grids d'un onboarding
 */
export async function adminUnlinkProject(onboardingId, projectId) {
  const result = await nodeAxiosFirebase({
    method: 'POST',
    url: 'private/onboarding',
    body: {
      path: '/onboarding/admin/projects/unlink',
      onboardingId,
      projectId,
    },
  });
  if (result?.errorData) throw new Error(result.errorData.message || 'Failed to unlink project');
  return result?.data?.data || result?.data || result;
}

/**
 * Admin — met à jour les liaisons d'un projet grids ou d'une tâche/nody interne (max 50)
 */
export async function adminUpdateProjectLinks({ projectId = null, taskId = null, links = [] } = {}) {
  const result = await nodeAxiosFirebase({
    method: 'POST',
    url: 'private/onboarding',
    body: {
      path: '/onboarding/admin/projects/links',
      projectId,
      taskId,
      links,
    },
  });
  if (result?.errorData) throw new Error(result.errorData.message || 'Failed to update links');
  return result?.data?.data || result?.data || result;
}

/**
 * Admin BOS — enfants d'un projet (tâches client, internes, billets)
 */
export async function adminGetProjectChildren(projectId) {
  const result = await nodeAxiosFirebase({
    method: 'POST',
    url: 'private/onboarding',
    body: {
      path: '/onboarding/admin/projects/children',
      projectId,
    },
  });
  if (result?.errorData) throw new Error(result.errorData.message || 'Failed to load project children');
  if (result?.error) throw new Error(result.message || 'Failed to load project children');
  return result?.data?.data || result?.data || result;
}

/**
 * Admin BOS — ajoute une tâche client ou un élément interne au projet
 */
export async function adminAddProjectChild({
  projectId,
  audience = 'client',
  name,
  description = '',
  type = null,
  priority = 'medium',
} = {}) {
  const result = await nodeAxiosFirebase({
    method: 'POST',
    url: 'private/onboarding',
    body: {
      path: '/onboarding/admin/projects/add-child',
      projectId,
      audience,
      name,
      description,
      type,
      priority,
    },
  });
  if (result?.errorData) throw new Error(result.errorData.message || 'Failed to add project child');
  if (result?.error) throw new Error(result.message || 'Failed to add project child');
  return result?.data?.data || result?.data || result;
}

/**
 * Liste les billets de service du portail
 */
export async function listServiceTickets(onboardingId, token = null, options = {}, clientBusinessId = null) {
  const result = await nodeAxiosFirebase({
    method: 'POST',
    url: 'https://public.usenode.com/onboarding/tickets/list',
    body: {
      ...(onboardingId && { onboardingId }),
      ...(token && { token }),
      ...(clientBusinessId && { clientBusinessId }),
    },
    ...options,
  });
  if (result?.errorData) {
    throw new Error(result.errorData?.data?.message || result.errorData.message || 'Failed to list tickets');
  }
  if (result?.error) throw new Error(result.message || 'Failed to list tickets');
  const tickets = result?.data;
  return Array.isArray(tickets) ? tickets : [];
}

/**
 * Structure billet Node (champs formulaire portail)
 */
export async function fetchTicketStructure(onboardingId = null, token = null, options = {}) {
  const result = await nodeAxiosFirebase({
    method: 'POST',
    url: 'https://public.usenode.com/onboarding/tickets/structure',
    body: { ...(onboardingId && { onboardingId }), ...(token && { token }) },
    ...options,
  });
  if (result?.errorData) {
    const msg =
      result.errorData?.data?.message ||
      result.errorData.message ||
      'Failed to load ticket structure';
    throw new Error(msg);
  }
  if (result?.error) throw new Error(result.message || 'Failed to load ticket structure');

  // { message, data: { fields } } | nested data.data | payload direct
  const payload =
    result?.data?.fields || result?.data?.structureId
      ? result.data
      : result?.data?.data?.fields || result?.data?.data?.structureId
        ? result.data.data
        : result?.fields
          ? result
          : result?.data?.data || result?.data || result;

  return payload;
}

/**
 * Crée un billet de service
 * @param {object} payload - { elementData, links, projectId } (+ legacy name/note/…)
 */
export async function createServiceTicket(onboardingId = null, payload = {}, token = null, options = {}) {
  const result = await nodeAxiosFirebase({
    method: 'POST',
    url: 'https://public.usenode.com/onboarding/tickets/create',
    body: {
      ...(onboardingId && { onboardingId }),
      ...(token && { token }),
      ...payload,
    },
    ...options,
  });
  if (result?.errorData) throw new Error(result.errorData.message || 'Failed to create ticket');
  if (result?.error) throw new Error(result.message || 'Failed to create ticket');
  return result?.data || result;
}

export async function fetchTicketLogs(ticketId, { onboardingId = null, clientBusinessId = null, token = null } = {}) {
  const result = await nodeAxiosFirebase({
    method: 'POST',
    url: 'https://public.usenode.com/onboarding/tickets/logs',
    body: {
      ticketId,
      ...(onboardingId && { onboardingId }),
      ...(clientBusinessId && { clientBusinessId }),
      ...(token && { token }),
    },
  });
  if (result?.error) throw new Error(result.message || 'Failed to fetch logs');
  return result?.data || [];
}

export async function fetchProjectChildren(projectId, { onboardingId = null, clientBusinessId = null, token = null } = {}) {
  const result = await nodeAxiosFirebase({
    method: 'POST',
    url: 'https://public.usenode.com/onboarding/projects/children',
    body: {
      projectId,
      ...(onboardingId && { onboardingId }),
      ...(clientBusinessId && { clientBusinessId }),
      ...(token && { token }),
    },
  });
  if (result?.error) throw new Error(result.message || 'Failed to fetch project children');
  return result?.data || {};
}

export async function addTicketLog(ticketId, message, { onboardingId = null, clientBusinessId = null, token = null } = {}) {
  const result = await nodeAxiosFirebase({
    method: 'POST',
    url: 'https://public.usenode.com/onboarding/tickets/logs/add',
    body: {
      ticketId,
      message,
      ...(onboardingId && { onboardingId }),
      ...(clientBusinessId && { clientBusinessId }),
      ...(token && { token }),
    },
  });
  if (result?.error) throw new Error(result.message || 'Failed to add log');
  return result?.data || null;
}

/**
 * Upload file for task
 * @param {string} onboardingId - The onboarding ID
 * @param {string} taskId - The task ID
 * @param {string} fileUrl - The uploaded file URL
 * @param {string} fileName - The file name
 * @param {number} fileSize - The file size in bytes
 * @param {object} options - Optional options
 */
export async function uploadTaskFile(onboardingId, taskId, fileUrl, fileName, fileSize, options = {}) {
  const result = await nodeAxiosFirebase({
    method: 'POST',
    url: 'https://public.usenode.com/onboarding/task/file',
    body: {
      onboardingId,
      taskId,
      fileUrl,
      fileName,
      fileSize,
    },
    noAuth: true,
    ...options,
  });

  if (result?.errorData) {
    throw new Error(result.errorData.message || 'Failed to upload task file');
  }

  return result;
}

/**
 * Update task status
 * @param {string} onboardingId - The onboarding ID
 * @param {string} taskId - The task ID
 * @param {string} status - The new status
 * @param {number} progress - Optional progress percentage
 * @param {object} options - Optional options
 */
export async function updateTaskStatus(onboardingId, taskId, status, progress = null, options = {}) {
  const result = await nodeAxiosFirebase({
    method: 'POST',
    url: 'https://public.usenode.com/onboarding/task',
    body: {
      onboardingId,
      taskId,
      status,
      progress,
    },
    noAuth: true,
    ...options,
  });

  if (result?.errorData) {
    throw new Error(result.errorData.message || 'Failed to update task');
  }

  return result;
}

/**
 * Send diagnostic report by email
 * @param {string} onboardingId - The onboarding ID
 * @param {object} options - Optional options
 */
export async function sendDiagnosticReport(onboardingId, options = {}) {
  const result = await nodeAxiosFirebase({
    method: 'POST',
    url: 'https://public.usenode.com/onboarding/email-report',
    body: { onboardingId },
    noAuth: true,
    ...options,
  });

  if (result?.errorData) {
    throw new Error(result.errorData.message || 'Failed to send report');
  }

  return result;
}

/**
 * Request a call with sales team
 * @param {string} onboardingId - The onboarding ID
 * @param {object} options - Optional options
 */
export async function requestCall(onboardingId, options = {}) {
  const result = await nodeAxiosFirebase({
    method: 'POST',
    url: 'https://public.usenode.com/onboarding/transition',
    body: {
      onboardingId,
      action: 'REQUEST_CALL',
    },
    noAuth: true,
    ...options,
  });

  if (result?.errorData) {
    throw new Error(result.errorData.message || 'Failed to request call');
  }

  return result;
}

/**
 * Update pricing addons selection
 * @param {string} onboardingId - The onboarding ID
 * @param {Array} selectedAddons - Array of selected addon IDs
 * @param {object} options - Optional options
 */
export async function updatePricingAddons(onboardingId, selectedAddons, options = {}) {
  const result = await nodeAxiosFirebase({
    method: 'PATCH',
    url: 'https://public.usenode.com/onboarding',
    body: {
      onboardingId,
      field: 'pricing.selectedAddons',
      value: selectedAddons,
    },
    noAuth: true,
    ...options,
  });

  if (result?.errorData) {
    throw new Error(result.errorData.message || 'Failed to update addons');
  }

  return result;
}

/**
 * Navigate to a specific state (for going back to completed steps)
 * @param {string} onboardingId - The onboarding ID
 * @param {string} targetState - The target state to navigate to
 * @param {object} options - Optional options
 */
export async function navigateToState(onboardingId, targetState, options = {}) {
  const result = await nodeAxiosFirebase({
    method: 'POST',
    url: 'https://public.usenode.com/onboarding/navigate',
    body: {
      onboardingId,
      targetState,
    },
    noAuth: true,
    ...options,
  });

  if (result?.errorData) {
    throw new Error(result.errorData.message || 'Failed to navigate to state');
  }

  return result;
}

/**
 * Sign contract
 * @param {string} onboardingId - The onboarding ID
 * @param {object} signatureData - Signature data (signature, acceptedTerms, etc.)
 * @param {object} options - Optional options
 */
export async function signContract(onboardingId, signatureData, options = {}) {
  const result = await nodeAxiosFirebase({
    method: 'POST',
    url: 'https://public.usenode.com/onboarding/transition',
    body: {
      onboardingId,
      action: 'SIGN',
      data: signatureData,
    },
    noAuth: true,
    showLoading: true,
    ...options,
  });

  if (result?.errorData) {
    throw new Error(result.errorData.message || 'Failed to sign contract');
  }

  return result;
}

// ---- Admin Atelier API ----

export async function adminListOnboardings(filters = {}) {
  const result = await nodeAxiosFirebase({
    method: 'POST',
    url: 'private/onboarding',
    body: { path: '/onboarding/admin/list', ...filters },
  });
  if (result?.errorData) throw new Error(result.errorData.message || 'Failed to list onboardings');
  return result?.data || result;
}

/**
 * Résout l'onboarding + projets BOS d'une entreprise (compte existant).
 * Sans onboarding: retourne quand même { onboardingId: null, projects }.
 * @param {string} businessId
 * @returns {Promise<{ onboardingId: string|null, projects: array, businessId: string }>}
 */
export async function fetchOnboardingByBusinessId(businessId) {
  if (!businessId) return { onboardingId: null, projects: [], businessId: null };
  const result = await nodeAxiosFirebase({
    method: 'POST',
    url: 'private/onboarding',
    body: { path: '/onboarding/admin/by-business', businessId },
  });
  if (result?.errorData) throw new Error(result.errorData.message || 'Failed to resolve onboarding');
  const data = result?.data || result;
  return {
    onboardingId: data?.onboardingId || null,
    projects: Array.isArray(data?.projects) ? data.projects : [],
    businessId: data?.businessId || businessId,
    hasOnboarding: data?.hasOnboarding !== false && Boolean(data?.onboardingId),
  };
}

/**
 * @deprecated Prefer fetchOnboardingByBusinessId — conserve le contrat string|null.
 */
export async function fetchOnboardingIdByBusinessId(businessId) {
  const data = await fetchOnboardingByBusinessId(businessId);
  return data.onboardingId;
}

export async function adminFetchOnboarding(onboardingId) {
  const result = await nodeAxiosFirebase({
    method: 'POST',
    url: 'private/onboarding',
    body: { path: '/onboarding/admin/by-id', onboardingId },
  });
  if (result?.errorData) throw new Error(result.errorData.message || 'Failed to fetch onboarding');
  return result?.data || result;
}

export async function adminUpdateState(onboardingId, currentState) {
  const result = await nodeAxiosFirebase({
    method: 'POST',
    url: 'private/onboarding',
    body: { path: '/onboarding/admin/update-state', onboardingId, currentState },
  });
  if (result?.errorData) throw new Error(result.errorData.message || 'Failed to update onboarding state');
  return result?.data || result;
}

export async function adminUpdateWorkshop(onboardingId, section, data) {
  const result = await nodeAxiosFirebase({
    method: 'POST',
    url: 'private/onboarding',
    body: { path: '/onboarding/admin/workshop/update', onboardingId, section, data },
  });
  if (result?.errorData) throw new Error(result.errorData.message || 'Failed to update workshop');
  return result?.data || result;
}

export async function adminUpdateVirageNumeriqueDiscount(onboardingId, enabled) {
  const result = await nodeAxiosFirebase({
    method: 'POST',
    url: 'private/onboarding',
    body: { path: '/onboarding/admin/pricing/virage', onboardingId, enabled },
  });
  if (result?.errorData) throw new Error(result.errorData.message || 'Failed to update Virage numérique discount');
  return result?.data || result;
}

export async function adminUpdateManualDiscount(onboardingId, discount) {
  const result = await nodeAxiosFirebase({
    method: 'POST',
    url: 'private/onboarding',
    body: { path: '/onboarding/admin/pricing/manual-discount', onboardingId, discount },
  });
  if (result?.errorData) throw new Error(result.errorData.message || 'Failed to update manual discount');
  if (result?.error) throw new Error(result.message || 'Failed to update manual discount');
  return result?.data || result;
}

export async function adminWorkshopNote(onboardingId, action, note = null, noteId = null) {
  const result = await nodeAxiosFirebase({
    method: 'POST',
    url: 'private/onboarding',
    body: { path: '/onboarding/admin/workshop/note', onboardingId, action, note, noteId },
  });
  if (result?.errorData) throw new Error(result.errorData.message || 'Failed to manage note');
  return result?.data || result;
}

export async function adminUpdateQualification(onboardingId, updates, icp) {
  const result = await nodeAxiosFirebase({
    method: 'POST',
    url: 'private/onboarding',
    body: { path: '/onboarding/admin/qualification/update', onboardingId, updates, icp },
  });
  if (result?.errorData) throw new Error(result.errorData.message || 'Failed to update qualification');
  return result?.data || result;
}

export async function adminUpdateSketch(onboardingId, section, data) {
  const result = await nodeAxiosFirebase({
    method: 'POST',
    url: 'private/onboarding',
    body: { path: '/onboarding/admin/sketch/update', onboardingId, section, data },
  });
  if (result?.errorData) throw new Error(result.errorData.message || 'Failed to update sketch');
  return result?.data || result;
}

export async function adminAnalyzeDiscovery(onboardingId, sourceText) {
  const result = await nodeAxiosFirebase({
    method: 'POST',
    url: 'private/onboarding',
    body: { path: '/onboarding/admin/discovery/analyze', onboardingId, sourceText },
  });
  if (result?.errorData) throw new Error(result.errorData.message || 'Failed to analyze discovery source');
  return result?.data || result;
}

// Demandes client (progress.structuredTasks) gérées depuis Atelier — réutilise les routes admin existantes.
export async function adminAddOnboardingTask(onboardingId, task, structureId = null, structureName = null) {
  const result = await nodeAxiosFirebase({
    method: 'POST',
    url: 'private/onboarding',
    body: { path: '/onboarding/admin/add-task', onboardingId, task, structureId, structureName },
  });
  if (result?.errorData) throw new Error(result.errorData.message || 'Failed to add onboarding task');
  return result?.data || result;
}

export async function adminUpdateOnboardingTask(onboardingId, taskId, updates) {
  const result = await nodeAxiosFirebase({
    method: 'POST',
    url: 'private/onboarding',
    body: { path: '/onboarding/admin/update-task', onboardingId, taskId, updates },
  });
  if (result?.errorData) throw new Error(result.errorData.message || 'Failed to update onboarding task');
  return result?.data || result;
}

export async function adminDeleteOnboardingTask(onboardingId, taskId) {
  const result = await nodeAxiosFirebase({
    method: 'POST',
    url: 'private/onboarding',
    body: { path: '/onboarding/admin/delete-task', onboardingId, taskId },
  });
  if (result?.errorData) throw new Error(result.errorData.message || 'Failed to delete onboarding task');
  return result?.data || result;
}

export async function adminSeedFromAnswers(onboardingId) {
  const result = await nodeAxiosFirebase({
    method: 'POST',
    url: 'private/onboarding',
    body: { path: '/onboarding/admin/sketch/seed-from-answers', onboardingId },
  });
  if (result?.errorData) throw new Error(result.errorData.message || 'Failed to seed from answers');
  return result?.data || result;
}

export async function adminGenerateBlueprint(onboardingId, userId) {
  const result = await nodeAxiosFirebase({
    method: 'POST',
    url: 'private/onboarding',
    body: { path: '/onboarding/admin/blueprint/generate', onboardingId, userId },
  });
  if (result?.errorData) throw new Error(result.errorData.message || 'Failed to generate blueprint');
  return result?.data || result;
}

export async function adminSendBlueprintEmail(onboardingId) {
  const result = await nodeAxiosFirebase({
    method: 'POST',
    url: 'private/onboarding',
    body: { path: '/onboarding/admin/blueprint/send-email', onboardingId },
  });
  if (result?.errorData) throw new Error(result.errorData.message || 'Failed to send blueprint email');
  return result?.data || result;
}

export async function adminSendFollowUpEmail(onboardingId) {
  const result = await nodeAxiosFirebase({
    method: 'POST',
    url: 'private/onboarding',
    body: { path: '/onboarding/admin/followup/send-email', onboardingId },
  });
  if (result?.errorData) throw new Error(result.errorData.message || 'Failed to send follow-up email');
  return result?.data || result;
}

export async function adminDeleteOnboarding({
  onboardingId,
  confirmation,
  confirmOnboardingId,
  confirmBusinessId,
}) {
  const result = await nodeAxiosFirebase({
    method: 'POST',
    url: 'private/onboarding',
    body: {
      path: '/onboarding/admin/delete',
      onboardingId,
      confirmation,
      confirmOnboardingId,
      confirmBusinessId,
    },
    options: { timeout: 300000 },
  });
  if (result?.errorData || result?.error) {
    throw new Error(
      result.errorData?.message || result.message || 'Failed to delete onboarding'
    );
  }
  return result?.data || result;
}

export async function adminGetOnboardingLogs(onboardingId) {
  const result = await nodeAxiosFirebase({
    method: 'POST',
    url: 'private/onboarding',
    body: { path: '/onboarding/admin/logs', onboardingId },
  });
  if (result?.errorData) throw new Error(result.errorData.message || 'Failed to fetch logs');
  return result?.data || result;
}

export async function adminValidateConversion(onboardingId) {
  const result = await nodeAxiosFirebase({
    method: 'POST',
    url: 'private/onboarding',
    body: { path: '/onboarding/admin/conversion/validate', onboardingId },
  });
  if (result?.errorData) throw new Error(result.errorData.message || 'Failed to validate conversion');
  return result?.data || result;
}

export async function adminRunConversion(onboardingId, dryRun = false, plan = null) {
  const result = await nodeAxiosFirebase({
    method: 'POST',
    url: 'private/onboarding',
    body: { path: '/onboarding/admin/conversion/run', onboardingId, dryRun, plan },
  });
  if (result?.errorData) throw new Error(result.errorData.message || 'Failed to run conversion');
  return result?.data || result;
}

export async function fetchPreviewAccess(onboardingId, token, options = {}) {
  const result = await nodeAxiosFirebase({
    method: 'POST',
    url: 'https://public.usenode.com/onboarding/preview-access',
    body: { onboardingId, token, lang: options.lang || 'fr', count: options.count || 10 },
    noAuth: true,
    ...options,
  });
  if (result?.errorData) throw new Error(result.errorData.message || 'Failed to load preview');
  return result?.data?.data || result?.data || result;
}

export async function fetchBlueprintPricing(onboardingId, token, options = {}) {
  const body = {
    onboardingId,
    token,
    businessTier: options.businessTier || 'standard',
  };
  if (options.pricingMode) body.pricingMode = options.pricingMode;
  if (typeof options.revisions === 'number') body.revisions = options.revisions;
  if (options.persistPricingMode) body.persistPricingMode = true;
  if (options.planTier) body.planTier = options.planTier;

  const result = await nodeAxiosFirebase({
    method: 'POST',
    url: 'https://public.usenode.com/onboarding/blueprint-pricing',
    body,
    ...clientAuth(),
  });
  if (result?.errorData) throw new Error(result.errorData.message || 'Failed to load pricing');
  return result?.data?.data || result?.data || result;
}

export function normalizePricingMode(mode) {
  if (mode === 'plancher') return 'floor';
  if (mode === 'juste') return 'fair';
  if (mode === 'floor' || mode === 'fair' || mode === 'premium') return mode;
  return 'fair';
}

export function getPriceForPricingMode(data, mode) {
  const normalized = normalizePricingMode(mode);
  if (typeof data?.recommendedPrice === 'number' && (!mode || normalizePricingMode(data?.pricingMode) === normalized)) {
    return data.recommendedPrice;
  }
  const range = data?.priceRange || {};
  if (normalized === 'floor') return range.floor || 0;
  if (normalized === 'premium') return range.premium || 0;
  return range.fair || 0;
}

// Export all functions as named exports and default object
export default {
  fetchOnboarding,
  findOnboarding,
  createDraft,
  createOnboarding,
  startOnboarding,
  transitionOnboarding,
  navigateToState,
  patchOnboarding,
  saveAnswer,
  updateTaskStatus,
  updateStructuredTask,
  uploadTaskFile,
  listProjects,
  updateProjectTask,
  adminLinkProject,
  adminUnlinkProject,
  adminUpdateProjectLinks,
  adminGetProjectChildren,
  adminAddProjectChild,
  listServiceTickets,
  fetchTicketStructure,
  createServiceTicket,
  sendDiagnosticReport,
  requestCall,
  updatePricingAddons,
  signContract,
  fetchOnboardingByBusinessId,
  fetchOnboardingIdByBusinessId,
  adminListOnboardings,
  adminFetchOnboarding,
  adminUpdateState,
  adminUpdateWorkshop,
  adminUpdateVirageNumeriqueDiscount,
  adminUpdateManualDiscount,
  adminUpdateQualification,
  adminWorkshopNote,
  adminUpdateSketch,
  adminAnalyzeDiscovery,
  adminAddOnboardingTask,
  adminUpdateOnboardingTask,
  adminDeleteOnboardingTask,
  adminSeedFromAnswers,
  adminGenerateBlueprint,
  adminSendBlueprintEmail,
  adminSendFollowUpEmail,
  adminGetOnboardingLogs,
  adminDeleteOnboarding,
  fetchTicketLogs,
  addTicketLog,
  fetchProjectChildren,
  adminValidateConversion,
  adminRunConversion,
  fetchPreviewAccess,
  fetchBlueprintPricing,
  getPriceForPricingMode,
};
